import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { HeroGallery } from '../components/HeroGallery';
import { Footer } from '../components/Footer';
import { AvailabilityCalendar } from '../components/AvailabilityCalendar';
import { SeasonalPricing } from '../components/SeasonalPricing';
import { ContactForm } from '../components/ContactForm';
import { WhatsAppWidget } from '../components/WhatsAppWidget';
import { MapPin, Check, Navigation, ShieldCheck, Clock, FileText, DollarSign, Calendar, Percent, Mail, MessageCircle, Users, Phone, User, Send, ChevronLeft, ChevronRight, Wifi, Wind, Flame, ChefHat, Package, Zap, UtensilsCrossed, Droplets, Coffee, RefreshCw, RotateCw, Sparkles, Fan, Tv, Monitor, Car, Waves, Droplet, Dumbbell, Thermometer, Sun, Leaf, Utensils, TreePine, Eye, Umbrella, Baby, Laptop, ArrowUpDown, Accessibility, KeyRound, BedDouble, Layers, PawPrint, LogIn, LogOut, Ban, VolumeX, PartyPopper, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { OSMMap } from '../components/OSMMap';
import { API } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { resolveLocalizedList, resolveLocalizedText, translateServiceLabel } from '../lib/i18n';
import { formatDateDDMMYYYY, formatEuroAmount } from '../lib/utils';

const SERVICE_ICONS = {
  'WiFi': Wifi,
  'Aire acondicionado': Wind,
  'Calefacción': Flame,
  'Cocina equipada': ChefHat,
  'Nevera': Package,
  'Microondas': Zap,
  'Horno': UtensilsCrossed,
  'Lavavajillas': Droplets,
  'Cafetera': Coffee,
  'Lavadora': RefreshCw,
  'Secadora': RotateCw,
  'Plancha': Sparkles,
  'Secador de pelo': Fan,
  'TV': Tv,
  'Smart TV': Monitor,
  'Parking gratuito': Car,
  'Piscina': Waves,
  'Piscina climatizada': Waves,
  'Jacuzzi': Droplet,
  'Gimnasio': Dumbbell,
  'Sauna': Thermometer,
  'Terraza/Balcón': Sun,
  'Jardín': Leaf,
  'Barbacoa': Utensils,
  'Patio exterior': TreePine,
  'Vistas al mar': Eye,
  'Primera línea de playa': Umbrella,
  'Cuna': Baby,
  'Trona para bebé': Baby,
  'Zona de trabajo': Laptop,
  'Chimenea': Flame,
  'Ascensor': ArrowUpDown,
  'Acceso sin escaleras': Accessibility,
  'Check-in autónomo': KeyRound,
  'Ropa de cama': BedDouble,
  'Toallas': Layers,
  'Admite mascotas': PawPrint,
};

const getRuleIcon = (rule) => {
  const lower = rule.toLowerCase();
  if (lower.startsWith('check-in')) return LogIn;
  if (lower.startsWith('check-out')) return LogOut;
  if (lower.includes('fumar') || lower.includes('smoking')) return Ban;
  if (lower.includes('mascota') || lower.includes('pet')) return PawPrint;
  if (lower.includes('ruido') || lower.includes('silencio') || lower.includes('quiet') || lower.includes('noise')) return VolumeX;
  if (lower.includes('niño') || lower.includes('child') || lower.includes('bebé') || lower.includes('baby')) return Baby;
  if (lower.includes('fiesta') || lower.includes('party')) return PartyPopper;
  if (lower.includes('limpie') || lower.includes('clean')) return Sparkles;
  return CheckCircle2;
};

const dynamicPageContent = {
  es: {
    loadingProperty: 'Cargando propiedad...',
    errorTitle: 'Error',
    propertyNotFound: 'Propiedad no encontrada',
    backToForm: 'Volver al formulario',
    editProperty: 'Editar Propiedad',
    showAllPhotos: 'Mostrar todas las fotos',
    close: 'Cerrar',
    travelers: 'viajeros',
    bedrooms: 'dormitorios',
    beds: 'camas',
    bathroom: 'baño',
    bathrooms: 'baños',
    propertyDescription: 'Descripción de la propiedad',
    services: 'Servicios',
    location: 'Ubicación',
    houseRules: 'Reglas de la casa',
    cancellationPolicy: 'Política de cancelación',
    availability: 'Disponibilidad',
    available: 'Disponible',
    booked: 'Reservado',
    pricing: 'Precios',
    minPrice: 'Precio mínimo',
    maxPrice: 'Precio máximo',
    perNight: 'Por noche',
    perNightSuffix: '/noche',
    basePrice: 'Precio base',
    seasonalRatesTitle: 'Tarifas por temporada',
    weekendPrice: 'Precio fin de semana',
    durationDiscounts: 'Descuentos por duración',
    weeklyStay: 'Estancia semanal (7+ noches)',
    monthlyStay: 'Estancia mensual (30+ noches)',
    priceRange: 'Rango de precios:',
    from: 'desde',
    to: 'hasta',
    seasonalPriceFallback: 'Precio por noche según temporada',
    requestInfo: 'Solicitar información',
    yourName: 'Tu nombre',
    yourEmail: 'Tu email',
    yourPhone: 'Tu teléfono (opcional)',
    guests: 'Número de huéspedes',
    checkInDate: 'Fecha de entrada',
    checkOutDate: 'Fecha de salida',
    message: 'Mensaje',
    sendInquiry: 'Enviar consulta',
    messageSent: '¡Mensaje enviado con éxito!',
    whatsappButton: 'Contactar por WhatsApp',
    whatsappText: '¡Hola! Me interesa',
    viewOnAirbnb: 'Ver en Airbnb',
    viewOnBooking: 'Ver en Booking.com',
    viewOnPlatform: 'Ver en plataforma',
    monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    dayNames: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  },
  en: {
    loadingProperty: 'Loading property...',
    errorTitle: 'Error',
    propertyNotFound: 'Property not found',
    backToForm: 'Back to form',
    editProperty: 'Edit Property',
    showAllPhotos: 'Show all photos',
    close: 'Close',
    travelers: 'guests',
    bedrooms: 'bedrooms',
    beds: 'beds',
    bathroom: 'bathroom',
    bathrooms: 'bathrooms',
    propertyDescription: 'Property description',
    services: 'Services',
    location: 'Location',
    houseRules: 'House rules',
    cancellationPolicy: 'Cancellation policy',
    availability: 'Availability',
    available: 'Available',
    booked: 'Booked',
    pricing: 'Pricing',
    minPrice: 'Minimum price',
    maxPrice: 'Maximum price',
    perNight: 'Per night',
    perNightSuffix: '/night',
    basePrice: 'Base price',
    seasonalRatesTitle: 'Seasonal rates',
    weekendPrice: 'Weekend price',
    durationDiscounts: 'Length-of-stay discounts',
    weeklyStay: 'Weekly stay (7+ nights)',
    monthlyStay: 'Monthly stay (30+ nights)',
    priceRange: 'Price range:',
    from: 'from',
    to: 'to',
    seasonalPriceFallback: 'Price per night according to season',
    requestInfo: 'Request information',
    yourName: 'Your name',
    yourEmail: 'Your email',
    yourPhone: 'Your phone (optional)',
    guests: 'Number of guests',
    checkInDate: 'Check-in date',
    checkOutDate: 'Check-out date',
    message: 'Message',
    sendInquiry: 'Send inquiry',
    messageSent: 'Message sent successfully!',
    whatsappButton: 'Contact on WhatsApp',
    whatsappText: 'Hello! I am interested in',
    viewOnAirbnb: 'View on Airbnb',
    viewOnBooking: 'View on Booking.com',
    viewOnPlatform: 'View on platform',
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  },
};

const getBookingLinkMeta = (url, labels) => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname.includes('airbnb.')) {
      return {
        label: labels.viewOnAirbnb,
        className: 'block w-full text-center py-2 px-4 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition',
      };
    }
    if (hostname.includes('booking.')) {
      return {
        label: labels.viewOnBooking,
        className: 'block w-full text-center py-2 px-4 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition',
      };
    }
  } catch {
    // Fallback for malformed URLs keeps behavior predictable.
  }

  return {
    label: labels.viewOnPlatform,
    className: 'block w-full text-center py-2 px-4 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition',
  };
};

const LANDING_SEO_DEFAULTS = {
  title: 'Mi Web Vacacional | Tu web para alquiler vacacional',
  description: 'Crea la pagina web profesional de tu alojamiento vacacional con un pago unico. Sin cuotas mensuales. Formulario simple, previsualizacion incluida y publicacion rapida.',
  image: 'https://www.miwebvacacional.online/assets/ogimage.png',
};

const upsertMetaTag = (attribute, key, content) => {
  if (!content) return;

  let tag = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

export const DynamicPropertyPage = ({ showEditButton = false, sampleProperty = null }) => {
  const { language } = useLanguage();
  const { license_number } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);

  const fetchProperty = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/properties/${license_number}`);
      setProperty(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching property:', err);
      setError('No se pudo cargar la propiedad');
      toast.error('Error al cargar la propiedad');
    } finally {
      setLoading(false);
    }
  }, [license_number]);

  const fetchCalendar = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${license_number}/calendar`);
      setBlockedDates(response.data.blocked_dates || []);
    } catch (err) {
      console.error('Error fetching calendar:', err);
    }
  }, [license_number]);

  useEffect(() => {
    if (sampleProperty) {
      setProperty(sampleProperty);
      setBlockedDates(sampleProperty.blocked_dates || []);
      setLoading(false);
      setError(null);
      return;
    }

    fetchProperty();
    if (license_number) {
      fetchCalendar();
    }
  }, [license_number, sampleProperty, fetchProperty, fetchCalendar]);

  // Dynamic SEO tags for preview/sample pages
  useEffect(() => {
    if (!property) return;

    const name = resolveLocalizedText(property.property_data?.name, language) || property.license_number;
    const descriptionSource = resolveLocalizedText(property.property_data?.description, language) || '';
    const description = descriptionSource.length > 155
      ? `${descriptionSource.slice(0, 152)}...`
      : (descriptionSource || LANDING_SEO_DEFAULTS.description);
    const pageTitle = `${name} | Mi Web Vacacional`;
    const shareImage = Array.isArray(property.images)
      ? property.images.map((image) => image?.url).find((url) => typeof url === 'string' && url.startsWith('http'))
      : null;

    document.title = pageTitle;
    upsertMetaTag('name', 'description', description);
    upsertMetaTag('property', 'og:title', pageTitle);
    upsertMetaTag('property', 'og:description', description);
    upsertMetaTag('property', 'og:image', shareImage || '');
    upsertMetaTag('name', 'twitter:title', pageTitle);
    upsertMetaTag('name', 'twitter:description', description);
    upsertMetaTag('name', 'twitter:image', shareImage || '');

    return () => {
      document.title = LANDING_SEO_DEFAULTS.title;
      upsertMetaTag('name', 'description', LANDING_SEO_DEFAULTS.description);
      upsertMetaTag('property', 'og:title', LANDING_SEO_DEFAULTS.title);
      upsertMetaTag('property', 'og:description', LANDING_SEO_DEFAULTS.description);
      upsertMetaTag('property', 'og:image', LANDING_SEO_DEFAULTS.image || '');
      upsertMetaTag('name', 'twitter:title', LANDING_SEO_DEFAULTS.title);
      upsertMetaTag('name', 'twitter:description', LANDING_SEO_DEFAULTS.description);
      upsertMetaTag('name', 'twitter:image', LANDING_SEO_DEFAULTS.image || '');
    };
  }, [property, language]);

  // JSON-LD structured data (LodgingBusiness)
  useEffect(() => {
    if (!property) return;
    const { property_data, images, location, contact } = property;
    const name = resolveLocalizedText(property_data?.name, language) || property.license_number;
    const description = resolveLocalizedText(property_data?.description, language) || '';
    const address = resolveLocalizedText(property_data?.location, language) || location?.google_maps_address || '';

    const jsonld = {
      '@context': 'https://schema.org',
      '@type': 'LodgingBusiness',
      name,
      description,
      address: { '@type': 'PostalAddress', streetAddress: address },
      ...(images?.length && { image: images.map(img => img.url).filter(Boolean) }),
      ...(location?.latitude && location?.longitude && {
        geo: { '@type': 'GeoCoordinates', latitude: location.latitude, longitude: location.longitude },
      }),
      ...(contact?.email && { email: contact.email }),
      ...(contact?.whatsapp && { telephone: contact.whatsapp }),
      ...(property_data?.stats?.bedrooms && { numberOfRooms: property_data.stats.bedrooms }),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'property-jsonld';
    script.textContent = JSON.stringify(jsonld);
    document.head.appendChild(script);

    return () => {
      document.getElementById('property-jsonld')?.remove();
    };
  }, [property, language]);

  const pageText = dynamicPageContent[language] || dynamicPageContent.es;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{pageText.loadingProperty}</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{pageText.errorTitle}</h1>
          <p className="text-gray-600 mb-4">{error || pageText.propertyNotFound}</p>
          <Button onClick={() => navigate('/submit')}>{pageText.backToForm}</Button>
        </div>
      </div>
    );
  }

  const { property_data, images, location, pricing, contact, services, booking, plan } = property;
  const localizedAddress = resolveLocalizedText(property_data?.location, language) || location?.google_maps_address || '';
  const localizedPropertyData = {
    ...property_data,
    name: resolveLocalizedText(property_data?.name, language),
    location: localizedAddress,
    description: resolveLocalizedText(property_data?.description, language),
    rules: resolveLocalizedList(property_data?.rules, language),
    cancellation_policy: resolveLocalizedText(property_data?.cancellation_policy, language),
    check_in: resolveLocalizedText(property_data?.check_in, language),
    check_out: resolveLocalizedText(property_data?.check_out, language),
  };
  const localizedLocation = {
    ...location,
    display_address: localizedAddress,
    area_description: resolveLocalizedText(location?.area_description, language),
    nearby: resolveLocalizedList(location?.nearby, language),
  };
  const localizedServices = (services || []).map((service) => ({
    key: service,
    label: translateServiceLabel(service, language),
  }));

  return (
    <div className="min-h-screen bg-white">
      <Header propertyName={localizedPropertyData.name} showLanguageToggle={plan !== 'basic'} />
      
      {/* Edit Button for Preview Page */}
      {showEditButton && (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/submit/${license_number}/edit`)}
          >
            {pageText.editProperty}
          </Button>
        </div>
      )}

      {/* Hero Gallery */}
      <HeroGalleryDynamic images={images} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Property Description */}
            <PropertyDescriptionDynamic 
              propertyData={localizedPropertyData}
              location={localizedLocation}
              services={localizedServices}
              licenseNumber={property.license_number}
              labels={pageText}
            />

            {/* Location Section */}
            <LocationSectionDynamic location={localizedLocation} labels={pageText} />

            {/* House Rules */}
            <HouseRulesDynamic rules={localizedPropertyData.rules} checkIn={localizedPropertyData.check_in} checkOut={localizedPropertyData.check_out} labels={pageText} />

            {/* Cancellation Policy */}
            <CancellationPolicyDynamic policy={localizedPropertyData.cancellation_policy} labels={pageText} />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 border rounded-xl p-6 shadow-sm bg-white">
              {/* Availability Calendar */}
              {(plan === 'plus' || plan === 'premium') && (
                <AvailabilityCalendarDynamic 
                  blockedDates={blockedDates}
                  licenseNumber={license_number}
                  labels={pageText}
                />
              )}

              {/* Seasonal Pricing */}
              <SeasonalPricingDynamic pricing={pricing} labels={pageText} />

              {/* Contact Form */}
              <ContactFormDynamic contact={contact} labels={pageText} licenseNumber={license_number} />

              {/* WhatsApp Widget */}
              <WhatsAppWidgetDynamic contact={contact} propertyName={localizedPropertyData.name} labels={pageText} />

              {/* Booking Links */}
              {booking.booking_links?.length > 0 && (
                <div className="pt-4 space-y-2">
                  {booking.booking_links.map((url, i) => {
                    const meta = getBookingLinkMeta(url, pageText);
                    return (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={meta.className}
                      >
                        {meta.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Dynamic Hero Gallery Component
const HeroGalleryDynamic = ({ images }) => {
  const { language } = useLanguage();
  const labels = dynamicPageContent[language] || dynamicPageContent.es;
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const displayImages = images.slice(0, 5);
  const mainImage = displayImages[0];
  const sideImages = displayImages.slice(1, 5);

  return (
    <>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative rounded-xl overflow-hidden group cursor-pointer">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 h-[400px] md:h-[500px]">
            <div className="relative col-span-1 h-full overflow-hidden" onClick={() => setShowAllPhotos(true)}>
              <img src={mainImage?.url} alt="Main" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="hidden md:grid grid-cols-2 gap-2 h-full">
              {sideImages.map((image, index) => (
                <div key={index} className="relative overflow-hidden" onClick={() => setShowAllPhotos(true)}>
                  <img src={image.url} alt={`Image ${index + 2}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              ))}
            </div>
          </div>
          <Button onClick={() => setShowAllPhotos(true)} variant="secondary" className="absolute bottom-4 right-4 bg-white hover:bg-gray-100 shadow-lg">
            {labels.showAllPhotos}
          </Button>
        </div>
      </div>

      {showAllPhotos && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 overflow-y-auto">
          <div className="container mx-auto px-4 py-8">
            <Button variant="ghost" className="text-white mb-4" onClick={() => setShowAllPhotos(false)}>
              {labels.close}
            </Button>
            <div className="grid grid-cols-1 gap-4">
              {images.map((image, index) => (
                <img key={index} src={image.url} alt={`Foto ${index + 1}`} className="w-full h-auto object-cover rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Dynamic Property Description
const PropertyDescriptionDynamic = ({ propertyData, location, services, licenseNumber, labels }) => {
  return (
    <div className="space-y-8 mb-8">
      <div className="space-y-1 pb-8 border-b">
        <div className="flex items-center gap-2 text-gray-600">
          <MapPin className="h-5 w-5" />
          <span className="text-lg">{location.display_address || location.google_maps_address}</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
          {propertyData.name}
          {licenseNumber && (
            <span className="inline-flex items-center gap-2 ml-2 align-middle text-base md:text-lg font-normal text-gray-600">
              <span aria-hidden="true">·</span>
              <span className="font-mono text-gray-600">{licenseNumber}</span>
            </span>
          )}
        </h1>
        <p className="text-base text-gray-700">
          {propertyData.stats.guests} {labels.travelers} · {propertyData.stats.bedrooms} {labels.bedrooms} · {propertyData.stats.beds} {labels.beds} · {propertyData.stats.bathrooms} {propertyData.stats.bathrooms > 1 ? labels.bathrooms : labels.bathroom}
        </p>
      </div>

      <div className="pb-8 border-b">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900">{labels.propertyDescription}</h2>
        <p className="text-gray-700 whitespace-pre-line leading-relaxed">{propertyData.description}</p>
      </div>

      {services.length > 0 && (
        <div className="pb-8 border-b">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900">{labels.services}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((svc, index) => {
              const IconCmp = SERVICE_ICONS[svc.key] || Check;
              return (
                <div key={index} className="flex items-center gap-3">
                  <IconCmp className="h-5 w-5 text-teal-600 flex-shrink-0" />
                  <span className="text-gray-700">{svc.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Dynamic Location Section
const LocationSectionDynamic = ({ location, labels }) => {
  const nearbyList = Array.isArray(location?.nearby) ? location.nearby : [];

  return (
    <div className="pb-8 border-b mb-8">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 flex items-center gap-2">
        <MapPin className="h-6 w-6" />
        {labels.location}
      </h2>
      
      {location?.area_description && (
        <p className="text-gray-700 leading-relaxed mb-6">{location.area_description}</p>
      )}
      
      {nearbyList.length > 0 && (
        <div className="space-y-3 mb-6">
          {nearbyList.map((detail, index) => (
            <div key={index} className="flex items-start gap-3">
              <Navigation className="h-4 w-4 text-teal-600 flex-shrink-0 mt-1" />
              <span className="text-gray-700">{detail}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mb-4">
        <OSMMap
          address={location?.google_maps_address}
          latitude={location?.latitude}
          longitude={location?.longitude}
          markerLabel={location?.display_address || location?.google_maps_address || 'Ubicacion del alojamiento'}
          heightClassName="h-96"
        />
      </div>
    </div>
  );
};

// Dynamic House Rules
const HouseRulesDynamic = ({ rules, checkIn, checkOut, labels }) => {
  const allRules = [
    `Check-in: ${checkIn}`,
    `Check-out: ${checkOut}`,
    ...rules
  ];

  return (
    <div className="pb-8 border-b mb-8">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 flex items-center gap-2">
        <ShieldCheck className="h-6 w-6" />
        {labels.houseRules}
      </h2>
      
      <div className="space-y-3 mb-4">
        {allRules.map((rule, index) => {
          const RuleIcon = getRuleIcon(rule);
          return (
            <div key={index} className="flex items-start gap-3">
              <RuleIcon className="h-4 w-4 text-teal-500 flex-shrink-0 mt-1" />
              <span className="text-gray-700">{rule}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Dynamic Cancellation Policy
const CancellationPolicyDynamic = ({ policy, labels }) => {
  if (!policy) return null;

  return (
    <div className="pb-8 mb-8">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900">
        {labels.cancellationPolicy}
      </h2>

      {(() => {
        const lines = policy.split('\n').map(l => l.trim()).filter(Boolean);
        return lines.length > 1 ? (
          <div className="space-y-3">
            {lines.map((line, index) => (
              <div key={index} className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-teal-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">{line}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-700 whitespace-pre-line leading-relaxed">{policy}</p>
        );
      })()}
    </div>
  );
};

// Dynamic Availability Calendar
const AvailabilityCalendarDynamic = ({ blockedDates, licenseNumber, labels }) => {
  const [month, setMonth] = useState(new Date());
  
  const blocked = blockedDates.map(dateStr => new Date(dateStr));

  const isDateBlocked = (date) => {
    return blocked.some(blockedDate => 
      blockedDate.getDate() === date.getDate() &&
      blockedDate.getMonth() === date.getMonth() &&
      blockedDate.getFullYear() === date.getFullYear()
    );
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1));
  };

  const nextMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1));
  };

  const daysInMonth = getDaysInMonth(month);
  const firstDay = getFirstDayOfMonth(month);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);
  const monthNames = labels.monthNames;
  const dayNames = labels.dayNames;

  return (
    <div className="pb-6 mb-6 border-b">
      <h3 className="text-xl font-semibold mb-4">{labels.availability}</h3>
      
      <div className="w-full bg-white rounded-lg">
        {/* Month/Year Header */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={previousMonth} className="text-gray-600 hover:text-gray-900">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h4 className="font-semibold text-gray-900">
            {monthNames[month.getMonth()]} {month.getFullYear()}
          </h4>
          <button onClick={nextMonth} className="text-gray-600 hover:text-gray-900">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {emptyDays.map((_, i) => (
            <div key={`empty-${i}`} className="h-8" />
          ))}
          {days.map((day) => {
            const date = new Date(month.getFullYear(), month.getMonth(), day);
            const blocked = isDateBlocked(date);
            return (
              <div
                key={day}
                className={`h-8 flex items-center justify-center text-xs rounded font-medium cursor-default ${
                  blocked
                    ? 'bg-red-100 text-red-700 border border-red-200'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-50 border border-gray-300 rounded"></div>
          <span className="text-gray-600">{labels.available}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
          <span className="text-gray-600">{labels.booked}</span>
        </div>
      </div>
    </div>
  );
};

// Dynamic Seasonal Pricing
const SeasonalPricingDynamic = ({ pricing, labels }) => {
  const isAdvanced = pricing?.pricing_mode === 'advanced';
  const basePrice = Number(pricing?.base_price) || 0;
  const hasMinPrice = pricing?.min_price !== null && pricing?.min_price !== undefined && pricing?.min_price !== '';
  const hasMaxPrice = pricing?.max_price !== null && pricing?.max_price !== undefined && pricing?.max_price !== '';
  const showSeasonalFallback = !isAdvanced && ((!hasMinPrice && !hasMaxPrice) || (Number(pricing?.min_price) === 0 && Number(pricing?.max_price) === 0));

  // Always show final nightly price for season entries
  const getSeasonNightPrice = (rate) => {
    if (rate.rate_type === 'fixed') return Number(rate.rate_value) || 0;
    return basePrice * (1 + (Number(rate.rate_value) || 0) / 100);
  };

  const getWeekendNightPrice = () => {
    if (!hasWeekendSurcharge) return basePrice;
    const value = Number(pricing.weekend_surcharge?.value) || 0;
    if (pricing.weekend_surcharge?.type === 'fixed') return basePrice + value;
    return basePrice * (1 + value / 100);
  };

  const hasSeasonalRates = isAdvanced && Array.isArray(pricing?.seasonal_rates) && pricing.seasonal_rates.length > 0;
  const hasWeekendSurcharge = isAdvanced && pricing?.weekend_surcharge?.enabled && Number(pricing.weekend_surcharge.value) > 0;
  const hasDiscounts = isAdvanced && (Number(pricing?.duration_discounts?.week) > 0 || Number(pricing?.duration_discounts?.month) > 0);

  return (
    <div className="pb-6 mb-6 border-b">
      <h3 className="text-xl font-semibold mb-4">
        {labels.pricing}
      </h3>

      {showSeasonalFallback ? (
        <p className="text-gray-700">{labels.seasonalPriceFallback}</p>
      ) : isAdvanced ? (
        <div className="space-y-5">
          {/* Base price */}
          {pricing?.base_price > 0 && (
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium text-gray-900">{labels.basePrice}</h4>
                <p className="text-sm text-gray-600">{labels.perNight}</p>
              </div>
              <p className="text-xl font-bold text-gray-900">{formatEuroAmount(pricing.base_price)}</p>
            </div>
          )}

          {/* Weekend price */}
          {hasWeekendSurcharge && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-700">{labels.weekendPrice}</span>
              <span className="font-semibold text-gray-900">{formatEuroAmount(getWeekendNightPrice())}{labels.perNightSuffix}</span>
            </div>
          )}

          {/* Seasonal rates */}
          {hasSeasonalRates && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{labels.seasonalRatesTitle}</h4>
              <div className="space-y-2">
                {pricing.seasonal_rates.map((rate) => (
                  <div key={rate.id} className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 bg-gray-50 rounded-lg px-3 py-2 text-sm">
                    <p className="font-medium text-gray-800 truncate">{rate.label}</p>
                    <span className="shrink-0 font-semibold text-gray-900">{formatEuroAmount(getSeasonNightPrice(rate))}{labels.perNightSuffix}</span>
                    <p className="col-span-2 text-gray-600 whitespace-nowrap">{formatDateDDMMYYYY(rate.date_start)} - {formatDateDDMMYYYY(rate.date_end)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Duration discounts */}
          {hasDiscounts && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{labels.durationDiscounts}</h4>
              <div className="space-y-1 text-sm">
                {Number(pricing.duration_discounts?.week) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-700">{labels.weeklyStay}</span>
                    <span className="font-semibold text-green-700">-{pricing.duration_discounts.week}%</span>
                  </div>
                )}
                {Number(pricing.duration_discounts?.month) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-700">{labels.monthlyStay}</span>
                    <span className="font-semibold text-green-700">-{pricing.duration_discounts.month}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Calculated min/max range */}
          {(hasMinPrice || hasMaxPrice) && (
            <p className="text-xs text-gray-500">
              {labels.priceRange}{' '}
              {hasMinPrice && `${labels.from} ${formatEuroAmount(pricing.min_price)}`}
              {hasMinPrice && hasMaxPrice && ' · '}
              {hasMaxPrice && `${labels.to} ${formatEuroAmount(pricing.max_price)}`}
              {' '}{labels.perNight.toLowerCase()}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {hasMinPrice && (
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium text-gray-900">{labels.minPrice}</h4>
                <p className="text-sm text-gray-600">{labels.perNight}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-gray-900">{formatEuroAmount(pricing.min_price)}</p>
              </div>
            </div>
          )}

          {hasMaxPrice && (
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium text-gray-900">{labels.maxPrice}</h4>
                <p className="text-sm text-gray-600">{labels.perNight}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-gray-900">{formatEuroAmount(pricing.max_price)}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Dynamic Contact Form
const ContactFormDynamic = ({ contact, labels, licenseNumber }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    guests: '',
    checkInDate: '',
    checkOutDate: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!licenseNumber) {
      toast.error('No se pudo identificar la propiedad');
      return;
    }

    try {
      await axios.post(`${API}/properties/${licenseNumber}/inquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        guests: formData.guests ? parseInt(formData.guests, 10) : null,
        message: formData.message,
        check_in_date: formData.checkInDate || null,
        check_out_date: formData.checkOutDate || null,
      });

      toast.success(labels.messageSent);
      setFormData({ name: '', email: '', phone: '', guests: '', checkInDate: '', checkOutDate: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo enviar la consulta');
    }
  };

  return (
    <div className="pb-6 mb-6 border-b">
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-green-600" />
        {labels.requestInfo}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">{labels.yourName}</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{labels.yourEmail}</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">{labels.yourPhone}</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="guests">{labels.guests}</Label>
          <Input
            id="guests"
            type="number"
            min="1"
            value={formData.guests}
            onChange={(e) => setFormData({...formData, guests: e.target.value})}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="checkInDate">{labels.checkInDate}</Label>
            <Input
              id="checkInDate"
              type="date"
              value={formData.checkInDate}
              onChange={(e) => setFormData({...formData, checkInDate: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="checkOutDate">{labels.checkOutDate}</Label>
            <Input
              id="checkOutDate"
              type="date"
              value={formData.checkOutDate}
              onChange={(e) => setFormData({...formData, checkOutDate: e.target.value})}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">{labels.message}</Label>
          <Textarea
            id="message"
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            rows={4}
            required
          />
        </div>

        <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700">
          <Send className="h-4 w-4 mr-2" />
          {labels.sendInquiry}
        </Button>
      </form>
    </div>
  );
};

// Dynamic WhatsApp Widget
const WhatsAppWidgetDynamic = ({ contact, propertyName, labels }) => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(`${labels.whatsappText} ${propertyName}`);
    const whatsappUrl = `https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Button
      onClick={handleWhatsAppClick}
      className="w-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-lg transition-all hover:shadow-xl"
      size="lg"
    >
      <MessageCircle className="h-5 w-5 mr-2" />
      {labels.whatsappButton}
    </Button>
  );
};
